import FieldRow from 'components/FormElements/FieldRow';
import Button from 'components/Button';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import CkEditor from 'components/CkEditor';
import Dropzone, { UploadedFile } from 'components/Dropzone';
import { z } from 'zod';
import { Control, Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useStyles from '../Common/styles';
import { FC } from 'react';
import Spacing from 'components/Spacing';
import ButtonGroup from 'components/ButtonGroup';
import NotifyInvestorsTooltip from 'components/NotifyInvestorsTooltip';
import useFileUpload from 'hooks/ui/useFileUpload';
import { FileType } from 'further-ui/utils';
import { UploadType } from 'further-types/files';
import { useNotification } from 'hooks/ui/useNotification';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import { useEffect } from 'react';
import { useGetExitStatementSampleUrl } from 'hooks/data/exit/useGetExitStatementSampleUrl';

const exitStatementSchema = z.object({
  statementDate: z.date({ required_error: 'Please select a statement date.' }),
  title: z.string().min(1, 'Please enter a statement title.'),
  summary: z.string().min(1, 'Please enter a statement summary.'),
  letter: z.string().min(1, `Please enter a manager's letter.`),
  notes: z.string().optional(),
  supportingFiles: z
    .array(
      z.object({
        name: z.string(),
        path: z.string(),
        preview: z.string(),
      }),
    )
    .optional(),
});

export type FormValues = Required<typeof exitStatementSchema>;
export type FormData = z.infer<FormValues>;

type Props = {
  onNext: (values: FormData) => void;
  defaultValues?: FormData;
  exitId?: string;
  exitAmendmentId?: string;
  notifyInvestors?: boolean;
  setNotifyInvestors: (value: boolean) => void;
  previousExitStatementForCompanyLegalNameExists?: boolean;
  includePreviousExitStatementsValue?: boolean;
  setIncludePreviousExitStatements: (value: boolean) => void;
  tradingName?: string;
};

const FormTextField: FC<{
  name: string;
  placeholder: string;
  control: Control<FormData>;
  doubleLine?: boolean;
}> = ({ name, control, placeholder, doubleLine }) => {
  return (
    <Controller
      //@ts-expect-error
      name={name}
      control={control}
      render={({ field: { ref, ...rest }, fieldState: { error } }) => (
        <TextField
          {...rest}
          inputRef={ref}
          variant="outlined"
          placeholder={placeholder}
          fullWidth
          error={!!error}
          helperText={error?.message}
          multiline={doubleLine}
          minRows={doubleLine ? 2 : 1}
        />
      )}
    />
  );
};

type SupportingFilesUploadProps = {
  files: Array<{ name: string; path: string; preview?: string }>;
  onChange: (
    files: Array<{
      name: string;
      path: string;
      preview?: string;
    }>,
  ) => void;
  setValue: any;
  fieldName: string;
};

const SupportingFilesUpload: FC<SupportingFilesUploadProps> = ({
  files,
  onChange,
  setValue,
  fieldName,
}) => {
  const { uploadFile } = useFileUpload();
  const { error } = useNotification();

  const handleFileUpload = async (uploadedFiles: Array<UploadedFile>) => {
    try {
      const uploadResults = await Promise.all(
        uploadedFiles.map(async (file) => {
          const result = await uploadFile(
            file,
            UploadType.ReportingAttachments,
          );
          if (result.error) {
            throw result.error;
          }
          return {
            name: file.name,
            path: result.data.filePath,
            preview: file?.preview,
          };
        }),
      );

      const updatedFiles = [...files, ...uploadResults];
      onChange(updatedFiles);
      setValue(fieldName, updatedFiles);
    } catch (_err) {
      error('Failed to process file. Please try again.');
    }
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    onChange(updatedFiles);
    setValue(fieldName, updatedFiles);
  };

  return (
    <Dropzone
      name="supportingFiles"
      accept={{
        [FileType.PDF]: ['.pdf'],
      }}
      maxFiles={10}
      onDropFile={handleFileUpload}
      files={files.map((file) => ({
        preview: file.preview || file.path,
        fileName: file.name,
      }))}
      onRemove={handleRemoveFile}
    />
  );
};

const UpdateExitStatementDetails = ({
  defaultValues,
  onNext,
  exitId,
  exitAmendmentId,
  notifyInvestors,
  setNotifyInvestors,
  previousExitStatementForCompanyLegalNameExists = false,
  includePreviousExitStatementsValue = false,
  setIncludePreviousExitStatements,
  tradingName,
}: Props) => {
  const { classes } = useStyles();
  const { getSampleUrlAsync, isPending: isGettingSampleUrl } =
    useGetExitStatementSampleUrl();
  const { error } = useNotification();

  const { handleSubmit, control, setValue, getValues } = useForm<FormData>({
    shouldFocusError: true,
    resolver: zodResolver(exitStatementSchema),
    criteriaMode: 'all',
    defaultValues,
  });

  // Preview button is enabled if we have an exitId (form is pre-populated from statement)
  const isPreviewEnabled = !!exitId;

  // Update form when statementDate becomes available from exit statement data
  useEffect(() => {
    if (defaultValues?.statementDate) {
      setValue('statementDate', defaultValues.statementDate);
    }
  }, [defaultValues?.statementDate, setValue]);

  const handlePreviewPdf = async () => {
    if (!exitId) return;

    const currentValues = getValues();
    if (!currentValues.statementDate) {
      error('Please select a statement date before previewing.');
      return;
    }

    try {
      await getSampleUrlAsync({
        exitId,
        exitAmendmentId,
        data: {
          title: currentValues.title || '',
          summary: currentValues.summary || '',
          letter: currentValues.letter || '',
          notes: currentValues.notes || '',
          supportingFiles:
            currentValues.supportingFiles?.map((f) => ({
              name: f.name,
              path: f.path,
            })) || [],
          statementDate: currentValues.statementDate,
          includePreviousExitStatements: includePreviousExitStatementsValue,
        },
        previewPdf: true,
      });
    } catch (_err) {
      error('Failed to generate PDF preview. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <Spacing>
        <FieldRow title="Statement date*" topTitle>
          <Controller
            name="statementDate"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <AppDatePicker
                value={value || null}
                onChange={onChange}
                error={!!error}
                helperText={error?.message}
                placeholder="Select statement date"
                fullWidth
                required
              />
            )}
          />
        </FieldRow>
        <FieldRow title="Statement title*" topTitle>
          <FormTextField
            name="title"
            placeholder="e.g. One of your companies, Company A, has sold to Acquirer A"
            control={control}
          />
        </FieldRow>
        <FieldRow
          title="Statement summary*"
          topTitle
          informationText="This is a two line preview of the statement that will show in each investor’s portal before they open the statement."
        >
          <FormTextField
            name="summary"
            placeholder="e.g. Company A has recently been acquired by Acquirer A. For full details on the exit, along with a breakdown of the exit financials, please click to see this exit statement."
            control={control}
            doubleLine
          />
        </FieldRow>
        <FieldRow
          title="Manager commentary and sign-off*"
          informationText="This is your introduction to the exit statement, in which you can provide overarching commentary about the exit. The system will automatically add “Dear {{name}}” at the beginning. You should add the sign-off you feel appropriate."
          topTitle
        >
          <Controller
            name="letter"
            control={control}
            render={({
              field: { value, name },
              fieldState: { error },
              formState: { isSubmitting },
            }) => (
              <div>
                <CkEditor
                  disabled={isSubmitting}
                  placeholder="A multi-paragraph cover letter, providing an overview of this exit."
                  onBlur={(value: string) => {
                    setValue(name, value);
                  }}
                  value={value}
                />
                {error && <p className={classes.error}>{error.message}</p>}
              </div>
            )}
          />
        </FieldRow>

        <FieldRow
          title="Notes"
          topTitle
          tooltipMaxWidth={500}
          tooltipText={
            <>
              If this exit is a loss, but is subject to an escrow or earnout
              which may in future make it a gain, you may wish to include here
              guidance that the proceeds are subject to change during the escrow
              / earnout period (and outline the length of that period).
              <br />
              <br />
              You may wish to explain to investors that, if they claim any loss
              now, it may be subject to change and repayment of claimed loss
              relief in future.
              <br />
              <br />
              You may also wish to note that earnout (not escrow) gains that are
              either uncertain or unlikely at the time of initial exit do not
              attract EIS treatment when paid, and may be subject to capital
              gains tax. Tax advice should be sought in this situation.
            </>
          }
        >
          <Controller
            name="notes"
            control={control}
            render={({
              field: { value, name },
              formState: { isSubmitting },
            }) => (
              <CkEditor
                disabled={isSubmitting}
                placeholder="Use this box to add any notes under the metrics table, such as explanations of fees or other exit specifics. If this exit generated a cash return, you could use this space to encourage rollovers or point users to their cash balance page to process a withdrawal."
                onBlur={(value: string) => {
                  setValue(name, value);
                }}
                value={value}
                extraLines={1}
              />
            )}
          />
        </FieldRow>

        <FieldRow
          title="Upload supporting document"
          topTitle
          informationText="Only PDF files are accepted. Files will be attached to the back of the exit statement PDF and will be downloadable in the portal version of the exit statement."
        >
          <Controller
            name="supportingFiles"
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <SupportingFilesUpload
                files={value || []}
                onChange={onChange}
                setValue={setValue}
                fieldName={name}
              />
            )}
          />
        </FieldRow>
        {previousExitStatementForCompanyLegalNameExists && (
          <FieldRow
            title="Append past exit statements"
            topTitle
            tooltipText={`If selected, past exit statements for companies with a matching full legal name to ${
              tradingName || 'this company'
            } will be appended to this statement for applicable investors within those past exits.`}
          >
            <Switch
              checked={!!includePreviousExitStatementsValue}
              onChange={(_, checked) =>
                setIncludePreviousExitStatements(!!checked)
              }
            />
          </FieldRow>
        )}
        <ButtonGroup>
          <NotifyInvestorsTooltip title="If toggled, the system will send an email to each applicable investor notifying them that a new exit statement is available to view and download on their investor portal.">
            <Switch
              checked={!!notifyInvestors}
              onChange={(_, checked) => setNotifyInvestors(!!checked)}
            />
          </NotifyInvestorsTooltip>
          <Button
            variant="outlined"
            color="primary"
            onClick={handlePreviewPdf}
            disabled={!isPreviewEnabled || isGettingSampleUrl}
            loading={isGettingSampleUrl}
            tooltip={
              previousExitStatementForCompanyLegalNameExists
                ? 'Click here to download a PDF preview of this exit statement. If you have selected to append past exit statements, they will not be appended to the preview PDF statement.'
                : undefined
            }
          >
            Preview PDF
          </Button>
          <Button type="submit" color="primary" variant="contained">
            Proceed to summary
          </Button>
        </ButtonGroup>
      </Spacing>
    </form>
  );
};
export default UpdateExitStatementDetails;
